import 'twin.macro';
import 'styled-components/macro';

import { useTrackAmplitudeEvent } from 'support/amplitude/amplitudeEvent';

import { Dot } from './Dot';

export interface CarouselPaginationProps {
  activeIndex?: number;
  className?: string;
  onActiveIndexChange: (newIndex: number) => void;
  size?: number;
}

/**
 * This component renders the carousel navigation dots.
 */
export const CarouselPagination = ({
  activeIndex = 0,
  className,
  onActiveIndexChange,
  size = 0,
}: CarouselPaginationProps) => {
  const trackAmplitudeEvent = useTrackAmplitudeEvent();

  if (size < 2) {
    return null;
  }

  const onClickSlide = (index: number) => {
    onActiveIndexChange(index);

    const slideCount = size;
    const slideNumber = index + 1;
    const slidesCompletedPct = Math.round((slideNumber / slideCount) * 10000) / 100;

    trackAmplitudeEvent({
      name: 'Lab - Notes - Navigated to slide',
      data: { slideCount, slideNumber, slidesCompletedPct, viaPagination: true },
    });
  };

  return (
    // ::before pushes the content out of the view if it doesn't fit the viewport:
    <div
      tw="flex flex-wrap h-6 overflow-hidden before:(content h-full)"
      aria-label="Carousel pagination"
      className={className}
      role="navigation"
    >
      <div tw="flex h-full items-center justify-center min-w-full">
        {Array.from({ length: size }, (_, dotIndex) => {
          const isCurrent = activeIndex === dotIndex;

          return (
            <Dot
              key={dotIndex}
              aria-current={isCurrent}
              aria-label={`Slide ${dotIndex + 1}`}
              isCurrent={isCurrent}
              onClick={() => onClickSlide(dotIndex)}
              role="button"
            />
          );
        })}
      </div>
    </div>
  );
};
